import type { ElementRef, SceneTracks, TimelineElement } from "@/timeline";
import type { BannerTransform } from "@/services/storage/types";
import { useBannerStore } from "@/banners/banner-store";

const TRANSFORM_PARAM_KEYS = [
	"transform.positionX",
	"transform.positionY",
	"transform.scaleX",
	"transform.scaleY",
	"transform.rotate",
] as const;

// Last-seen transform per element id, so trackBannerTransforms only writes to the banner library
// when something actually changed — the reactor that calls it (see core/index.ts) fires after
// EVERY command in the whole app, most of which have nothing to do with a banner's position.
// Module-level and never pruned: a stale entry for a since-deleted element is harmless (it's just
// never looked up again), and pruning it would need hooking element-deletion too for no real gain.
const lastKnownTransformByElementId = new Map<string, string>();

function readTransform({
	element,
}: {
	element: TimelineElement;
}): BannerTransform | null {
	if (!("params" in element)) return null;
	const params = element.params;
	const values = TRANSFORM_PARAM_KEYS.map((key) => params[key]);
	if (!values.every((value): value is number => typeof value === "number")) {
		return null;
	}
	const [positionX, positionY, scaleX, scaleY, rotate] = values;
	return {
		"transform.positionX": positionX,
		"transform.positionY": positionY,
		"transform.scaleX": scaleX,
		"transform.scaleY": scaleY,
		"transform.rotate": rotate,
	};
}

function findElement({
	tracks,
	elementId,
}: {
	tracks: SceneTracks;
	elementId: string;
}): TimelineElement | undefined {
	const allTracks = [...tracks.overlay, tracks.main, ...tracks.audio];
	for (const track of allTracks) {
		const element = track.elements.find((el) => el.id === elementId);
		if (element) return element;
	}
	return undefined;
}

/** Called from the reactor registered in core/index.ts, after every command — checks whichever
 * elements are currently selected for a banner-sourced image/video (see
 * VideoElement.sourceBannerId / ImageElement.sourceBannerId) whose transform just settled to a
 * new value, and remembers it as that banner's new default position/scale/rotation (see
 * banner-store.ts's rememberTransform) — so the SAME banner starts there next time it's added to
 * any project. Scoped to the selection rather than every element on the timeline: cheap (this runs
 * after every single command app-wide), and matches how a user actually interacts with an
 * element's transform — via the canvas drag/resize handles or the properties panel, both of which
 * operate on the current selection. */
export function trackBannerTransforms({
	tracks,
	selectedElements,
}: {
	tracks: SceneTracks;
	selectedElements: readonly ElementRef[];
}): void {
	for (const ref of selectedElements) {
		const element = findElement({ tracks, elementId: ref.elementId });
		if (!element || (element.type !== "image" && element.type !== "video")) {
			continue;
		}
		const sourceBannerId = element.sourceBannerId;
		if (!sourceBannerId) continue;

		const transform = readTransform({ element });
		if (!transform) continue;

		const serialized = JSON.stringify(transform);
		if (lastKnownTransformByElementId.get(element.id) === serialized) continue;
		lastKnownTransformByElementId.set(element.id, serialized);

		void useBannerStore.getState().rememberTransform({ id: sourceBannerId, transform });
	}
}
